type Chip = {
  /** Width of the chip. */
  width?: number;
};

type ChipGroupState = {
  chips: Record<string, Chip>;
  control?: { width?: number; show?: 'auto' | 'always' };
};

export type BasicChipPayload = {
  /** A unique identifier for this chip. */
  uniqueId: string;
};

type AddChipPayload = BasicChipPayload & Partial<Chip>;

type UpdateChipPayload = BasicChipPayload & Partial<AddChipPayload>;

export const enum ChipGroupActions {
  ADDCHIP = 'add',
  UPDATECHIP = 'update',
  REMOVECHIP = 'remove',
  ADDCONTROL = 'add-control',
  UPDATECONTROL = 'update-control',
  REMOVECONTROL = 'remove-control',
}

export type ChipGroupReducerActions =
  | { type: ChipGroupActions.ADDCHIP; payload: AddChipPayload }
  | {
      type: ChipGroupActions.UPDATECHIP;
      payload: UpdateChipPayload;
    }
  | { type: ChipGroupActions.REMOVECHIP; payload: BasicChipPayload }
  | { type: ChipGroupActions.ADDCONTROL; payload: { width: number } }
  | {
      type: ChipGroupActions.UPDATECONTROL;
      payload: { width?: number; show?: 'auto' | 'always' };
    }
  | { type: ChipGroupActions.REMOVECONTROL; payload: Record<string, never> };

/** ChipGroup reducer function used for managing the state of the chips. */
export function chipGroupReducer(
  state: ChipGroupState,
  action: ChipGroupReducerActions,
): ChipGroupState {
  const { type, payload } = action;

  const newState = { ...state };

  // ADD Chip
  if (type === ChipGroupActions.ADDCHIP) {
    const { width } = payload;
    newState.chips = { ...newState.chips, [payload.uniqueId]: { width } };

    return newState;
  }

  // UPDATE Chip
  if (type === ChipGroupActions.UPDATECHIP) {
    const { uniqueId: _, ...chipState } = payload;

    newState.chips = {
      ...newState.chips,
      [payload.uniqueId]: {
        ...newState.chips[payload.uniqueId],
        ...chipState,
      },
    };

    return newState;
  }

  // REMOVE Chip
  if (type === ChipGroupActions.REMOVECHIP) {
    delete newState.chips[payload.uniqueId];

    return newState;
  }

  // ADD Control
  if (type === ChipGroupActions.ADDCONTROL) {
    const { width } = payload;
    newState.control = { width, show: 'auto' };

    return newState;
  }

  // UPDATE Control
  if (type === ChipGroupActions.UPDATECONTROL) {
    const { width, show } = payload;
    newState.control = { width, show };

    return newState;
  }

  // REMOVE Control
  if (type === ChipGroupActions.REMOVECONTROL) {
    newState.control = undefined;

    return newState;
  }

  return state;
}
